import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { noticeAPI } from '../services/api';
import { Notice } from '../types';
import { formatKstDateTime } from '../utils/datetime';

const Notices: React.FC = () => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editingNotice, setEditingNotice] = useState<Notice | null>(null);

  const { register, handleSubmit, reset, setValue } = useForm<{
    title: string;
    content: string;
  }>();

  // 공지사항 목록 조회
  const { data: notices, isLoading } = useQuery({
    queryKey: ['notices'],
    queryFn: async () => {
      const response = await noticeAPI.getAll();
      return response.data || [];
    },
  });

  // 공지사항 생성
  const createMutation = useMutation({
    mutationFn: noticeAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notices'] });
      handleClose();
    },
  });

  // 공지사항 수정
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Notice> }) =>
      noticeAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notices'] });
      handleClose();
    },
  });

  // 공지사항 삭제
  const deleteMutation = useMutation({
    mutationFn: noticeAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notices'] });
    },
  });

  const handleOpen = (notice?: Notice) => {
    if (notice) {
      setEditingNotice(notice);
      setValue('title', notice.title);
      setValue('content', notice.content);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingNotice(null);
    reset();
  };

  const onSubmit = (data: any) => {
    if (editingNotice) {
      updateMutation.mutate({
        id: editingNotice.id,
        data,
      });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('정말로 삭제하시겠습니까?')) {
      deleteMutation.mutate(id);
    }
  };

  const formatDate = (dateString: string) => formatKstDateTime(dateString);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">공지사항 관리</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          공지사항 추가
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>제목</TableCell>
              <TableCell>작성일</TableCell>
              <TableCell>상태</TableCell>
              <TableCell align="center">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {notices?.map((notice) => (
              <TableRow key={notice.id}>
                <TableCell>{notice.id}</TableCell>
                <TableCell>{notice.title}</TableCell>
                <TableCell>{formatDate(notice.createdAt)}</TableCell>
                <TableCell>
                  <Chip
                    label={notice.isActive ? '활성' : '비활성'}
                    color={notice.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton onClick={() => handleOpen(notice)} size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(notice.id)} size="small" color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>{editingNotice ? '공지사항 수정' : '공지사항 추가'}</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="제목"
              fullWidth
              required
              {...register('title', { required: true })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="내용"
              fullWidth
              multiline
              rows={10}
              required
              {...register('content', { required: true })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>취소</Button>
            <Button type="submit" variant="contained">
              {editingNotice ? '수정' : '추가'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Notices; 